import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { usePublisherReviewService } from '../../contexts/PublisherReviewServiceContext';
import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { ContentEditorService } from '../../services/ContentEditorService';
import { InlineCommentManager } from '../../services/InlineCommentManager';
import { log } from '../../loggers/Logger';
import { isNotFoundError } from './isNotFoundError';
import { useLoadedDocument } from './useLoadedDocument';
import { LoadedDocument } from '../../model/LoadedDocument';

/** Whether the content page edits the draft or only views it. */
export type ContentLoadMode = 'edit' | 'read';

/** The current load state for a content page. */
export interface ContentLoadState {
  /** The loaded document, or undefined while loading or on error. */
  readonly loadedDocument: LoadedDocument | undefined;
  /** True until the initial open resolves (or fails). */
  readonly isLoading: boolean;
  /** Display-ready error message, or undefined when no error or when not found. */
  readonly errorMessage: string | undefined;
  /** True when the content is missing or the viewer is not authorized to see it. */
  readonly isNotFound: boolean;
}

/**
 * Opens the document on the editor service — for editing (`edit-draft`) or
 * read-only (`read-draft`) — then opens the inline comment manager in draft
 * mode against the same workflow. The comment manager needs the
 * `draftWorkflowId` from the loaded handle, so this runs strictly after the
 * open resolves.
 *
 * @param signal Cancellation flag checked between async steps. If the
 *   caller flips `signal.cancelled` before the second step starts, the
 *   comment manager is never opened — preventing a leak when the effect's
 *   cleanup fires mid-flight.
 * @throws if either service rejects, or if the loaded handle does not carry a
 *   draft workflow (both draft opens are expected to always produce one — the
 *   throw surfaces the unexpected shape rather than silently skipping the
 *   comment load).
 */
async function openContentAndComments(
  service: ContentEditorService,
  commentManager: InlineCommentManager,
  contentId: string,
  mode: ContentLoadMode,
  signal: { cancelled: boolean },
): Promise<void> {
  const kind = mode === 'edit' ? 'edit-draft' : 'read-draft';
  await service.open(contentId, { kind });
  // Cleanup may have already closed the service. Bail out before we open
  // the comment manager that nobody will close.
  if (signal.cancelled) return;
  const handle = service.loadedDocumentHandle;
  if (!handle || handle.document.kind !== 'draft') {
    throw new Error(`Editor service did not produce a draft handle after ${kind} open.`);
  }
  const workflowId = handle.document.workflow.draftWorkflowId;
  await commentManager.open(contentId, { kind: 'draft', workflowId });
}

/**
 * Opens the content for the `:contentId` route param on the
 * {@link ContentEditorService} — for editing when `mode` is `edit`,
 * read-only when `read` — loads the inline comments for the draft workflow,
 * and binds the caller to the service's loaded document. On unmount or
 * contentId change, both services are closed. Shared by the content editor
 * and viewer pages, which differ only in how they present the loaded document.
 */
export function useDraftContentLoad(mode: ContentLoadMode): ContentLoadState {
  // Services + route input
  const service = useContentEditorService();
  const commentManager = useInlineCommentManager();
  const qualityCheckService = useQualityCheckService();
  const publisherReviewService = usePublisherReviewService();
  const { contentId } = useParams<{ contentId: string }>();

  // States
  const [isLoading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isNotFound, setNotFound] = useState(false);
  const loadedDocument = useLoadedDocument();

  // When contentId changes, open the content once and load its inline comments;
  // close both on unmount / contentId change. open/closeAndDiscard are
  // expensive, so we never re-run them on re-renders.
  useEffect(() => {
    // Shared cancel flag: read inside the async IIFE and by the helper
    // between its two awaits; flipped to true by the cleanup below.
    const cancelSignal = { cancelled: false };

    setLoading(true);
    setErrorMessage(undefined);
    setNotFound(false);

    // Both scan services outlive this page, so drop the previous content's scan
    // state before this one loads — otherwise its results and still-running poll
    // cycle surface against the content opened here.
    qualityCheckService.resetState();
    publisherReviewService.resetState();

    (async () => {
      try {
        await openContentAndComments(service, commentManager, contentId, mode, cancelSignal);
      } catch (e) {
        if (cancelSignal.cancelled) return;
        log.error({ err: e, contentId }, 'Failed to load content');
        // A forbidden/missing content renders the not-found page instead of a
        // generic error, so surface it as not-found rather than a message.
        if (isNotFoundError(e)) {
          setNotFound(true);
          return;
        }
        setErrorMessage(
          `Failed to load content: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      } finally {
        if (!cancelSignal.cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelSignal.cancelled = true;
      // Stop any in-flight quality check poll before the content closes below;
      // otherwise it keeps polling a document that is no longer loaded.
      qualityCheckService.resetState();

      // Fire-and-forget: React cleanup must be synchronous. Log but swallow
      // errors so unmount doesn't produce unhandled rejections. `closeAndDiscard`
      // is safe when nothing is open (e.g., when cleanup beats `commentManager.open`).
      commentManager.close().catch((e) => {
        log.warn({ error: e, contentId }, 'Failed to close inline comments on unmount');
      });
      service.closeAndDiscard().catch((e) => {
        log.warn({ error: e, contentId }, 'Failed to close content on unmount');
      });
    };
  }, [service, commentManager, qualityCheckService, publisherReviewService, contentId, mode]);

  return { loadedDocument, isLoading, errorMessage, isNotFound };
}
